import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';

import logger from '../services/logger';

const AuthContext = createContext({});

const USER_CACHE_KEY = 'obgh_user_cache';
const readCachedUser = () => {
  try { return JSON.parse(localStorage.getItem(USER_CACHE_KEY) || 'null'); } catch { return null; }
};
const writeCachedUser = (u) => {
  try { u ? localStorage.setItem(USER_CACHE_KEY, JSON.stringify(u)) : localStorage.removeItem(USER_CACHE_KEY); } catch { /* ignore */ }
};

export const AuthProvider = ({ children }) => {
  // Démarrage instantané en forêt : si un profil est en cache avec un jeton, on ouvre l'app tout de suite
  // et on revalide en arrière-plan. Déconnexion seulement si le serveur répond 401 (jeton invalide), jamais sur panne réseau.
  const hasToken = !!localStorage.getItem('obgh_token');
  const cachedUser = hasToken ? readCachedUser() : null;
  const [user, setUserState] = useState(cachedUser);
  const [loading, setLoading] = useState(!(hasToken && cachedUser));
  const setUser = useCallback((u) => { setUserState(u); writeCachedUser(u); }, []);

  const checkAuth = useCallback(async () => {
    if (!localStorage.getItem('obgh_token')) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const response = await api.get('/auth/me');
      setUser(response.data.user);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403 || status === 404) {
        localStorage.removeItem('obgh_token');
        setUser(null);
      }
      // Pas de réponse (signal faible / hors-ligne) → on garde le profil en cache.
    }
    setLoading(false);
  }, [setUser]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { token, user: userData } = response.data;
    if (token) localStorage.setItem('obgh_token', token);
    setUser(userData);
    return userData;
  }, [setUser]);

  const register = useCallback(async (email, password, name) => {
    // Auto-attach referral code if one was stashed when the user arrived via ?ref=
    let referral_code = '';
    try { referral_code = localStorage.getItem('obgh_ref_code') || ''; } catch (_) {}
    const response = await api.post('/auth/register', { email, password, name, referral_code });
    const { token, user: userData } = response.data;
    if (token) localStorage.setItem('obgh_token', token);
    // Consume the referral cookie once it's been transmitted.
    try { localStorage.removeItem('obgh_ref_code'); } catch (_) {}
    setUser(userData);
    return userData;
  }, [setUser]);

  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); } catch (err) { logger.warn('Logout failed:', err.message); }
    localStorage.removeItem('obgh_token');
    setUser(null);
  }, [setUser]);

  const value = useMemo(() => ({ user, login, register, logout, loading, checkAuth }), [user, login, register, logout, loading, checkAuth]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
